import { useState, useCallback, useMemo } from 'react';

interface UseImageZoomAndDragOptions {
  maxScale?: number;
  minScale?: number;
  scaleStep?: number;
  initialScale?: number;
  xLimitMultiplier?: number;
  yLimitMultiplier?: number;
  wheelZoomStep?: number;
}

interface Position {
  x: number;
  y: number;
}

export const useImageZoomAndDrag = (options: UseImageZoomAndDragOptions = {}) => {
  const {
    maxScale = 3,
    minScale = 1,
    scaleStep = 0.5,
    initialScale = 1,
    xLimitMultiplier = 200,
    yLimitMultiplier = 150,
    wheelZoomStep = 0.2,
  } = options;

  const [scale, setScale] = useState(initialScale);
  const [isDragging, setIsDragging] = useState(false);
  const [lastCords, setLastCords] = useState<Position>({ x: 0, y: 0 });

  const resetImageState = useCallback(() => {
    setScale(initialScale);
    setLastCords({ x: 0, y: 0 });
  }, [initialScale]);

  const handleZoom = useCallback(
    (direction: 'in' | 'out') => () => {
      if (direction === 'in' && scale < maxScale) {
        setScale((prev) => prev + scaleStep);
      }
      if (direction === 'out') {
        setScale((prevScale) => {
          const newScale = prevScale - scaleStep;
          if (newScale <= minScale) {
            setLastCords({ x: 0, y: 0 });
          }
          return Math.max(newScale, minScale);
        });
      }
    },
    [scale, maxScale, minScale, scaleStep]
  );

  const handleWheelZoom = useCallback(
    (e: React.WheelEvent<HTMLImageElement>) => {
      e.preventDefault();

      const delta = e.deltaY > 0 ? -wheelZoomStep : wheelZoomStep;
      const newScale = Math.max(minScale, Math.min(maxScale, scale + delta));

      setScale(newScale);

      if (newScale <= minScale) {
        setLastCords({ x: 0, y: 0 });
      }
    },
    [scale, maxScale, minScale, wheelZoomStep]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLImageElement>) => {
      if (!isDragging || scale <= minScale) return;

      const xLimit = xLimitMultiplier * (scale - 1);
      const yLimit = yLimitMultiplier * (scale - 1);
      const newX = Math.max(Math.min(lastCords.x + e.movementX, xLimit), -xLimit);
      const newY = Math.max(Math.min(lastCords.y + e.movementY, yLimit), -yLimit);

      setLastCords({ x: newX, y: newY });
    },
    [isDragging, scale, lastCords, xLimitMultiplier, yLimitMultiplier, minScale]
  );

  const handleMouseDown = useCallback(() => {
    if (scale > minScale) {
      setIsDragging(true);
    }
  }, [scale, minScale]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const imageTransform = useMemo(
    () => ({
      transform: `scale(${scale}) translate(${lastCords.x}px, ${lastCords.y}px)`,
      cursor: scale > minScale ? (isDragging ? 'grabbing' : 'grab') : 'default',
      transition: isDragging ? 'none' : 'transform 0.2s ease',
    }),
    [scale, lastCords.x, lastCords.y, isDragging, minScale]
  );

  const imageEventHandlers = useMemo(
    () => ({
      onMouseMove: handleMouseMove,
      onMouseDown: handleMouseDown,
      onMouseUp: handleMouseUp,
      onMouseLeave: handleMouseLeave,
      onWheel: handleWheelZoom,
      draggable: false,
    }),
    [handleMouseMove, handleMouseDown, handleMouseUp, handleMouseLeave, handleWheelZoom]
  );

  const canZoomIn = scale < maxScale;
  const canZoomOut = scale > minScale;

  return {
    scale,
    isDragging,
    lastCords,
    handleZoom,
    resetImageState,
    setScale,
    setLastCords,
    imageTransform,
    imageEventHandlers,
    canZoomIn,
    canZoomOut,
    isZoomed: scale > minScale,
  };
};
